import { 
  Calculator,
  BarChart,
  LineChart,
  Sigma,
  Percent,
  Bell,
  ArrowUpDown,
  BarChartHorizontal,
  Ruler,
  Dices,
  Diff,
  Asterisk,
  Hash,
  Zap,
  PieChart,
  ScatterChart,
  AreaChart,
  Radar,
  Workflow,
  Layers,
  CircleDot,
  Flower2,
  Columns,
  LucideIcon,
  ArrowRightLeft,
  Brackets,
  Shrink,
  Expand,
  SlidersHorizontal,
  Ratio
} from 'lucide-react';
import { CalculatorId, CalculatorType } from './calculator-config';

interface IconInfo {
  icon: LucideIcon;
  color: string;
}

// Map calculator types to appropriate icons and colors
const calculatorTypeIcons: Record<string, IconInfo> = {
  [CalculatorType.Graph]: {
    icon: BarChart,
    color: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
  },
  [CalculatorType.Table]: {
    icon: Calculator,
    color: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
  }
};

// Add a distribution type icon
calculatorTypeIcons['distribution'] = {
  icon: Bell,
  color: 'bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400'
};

// Add a statistics type icon
calculatorTypeIcons['statistics'] = {
  icon: Calculator,
  color: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
};

// Map specific calculator IDs to custom icons and colors
const calculatorIdIcons: Record<string, IconInfo> = {
  // Statistics calculators
  [CalculatorId.MEAN]: {
    icon: Calculator,
    color: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
  },
  [CalculatorId.MEDIAN]: {
    icon: ArrowUpDown,
    color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400'
  },
  [CalculatorId.MODE]: {
    icon: Hash,
    color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400'
  },
  [CalculatorId.RANGE]: {
    icon: Ruler,
    color: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
  },
  [CalculatorId.STANDARD_DEVIATION]: {
    icon: Sigma,
    color: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400'
  },
  [CalculatorId.VARIANCE]: {
    icon: Diff,
    color: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400'
  },
  [CalculatorId.SUMMARY]: {
    icon: BarChart,
    color: 'bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400'
  },
  [CalculatorId.Z_SCORE]: {
    icon: Zap,
    color: 'bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400'
  },
  [CalculatorId.PERCENTILE]: {
    icon: Percent,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.MEAN_ABSOLUTE_DEVIATION]: {
    icon: ArrowUpDown,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.MEDIAN_ABSOLUTE_DEVIATION]: {
    icon: ArrowUpDown,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  
  // Distribution calculators - ensure these have proper colors
  [CalculatorId.NORMAL_DISTRIBUTION]: {
    icon: Bell,
    color: 'bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400'
  },
  [CalculatorId.BINOMIAL_DISTRIBUTION]: {
    icon: Dices,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.POISSON_DISTRIBUTION]: {
    icon: Dices,
    color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400'
  },
  [CalculatorId.CHI_SQUARE]: {
    icon: Asterisk,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.ANOVA]: {
    icon: BarChartHorizontal,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.KURTOSIS]: {
    icon: LineChart,
    color: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400'
  },
  
  // Advanced Statistics calculators
  [CalculatorId.CORRELATION_COEFFICIENT]: {
    icon: ArrowRightLeft,
    color: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
  },
  [CalculatorId.COVARIANCE]: {
    icon: Workflow,
    color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400'
  },
  [CalculatorId.COEFFICIENT_OF_VARIATION]: {
    icon: Ratio,
    color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400'
  },
  [CalculatorId.CONFIDENCE_INTERVAL]: {
    icon: Brackets,
    color: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
  },
  [CalculatorId.FIVE_NUMBER_SUMMARY]: {
    icon: SlidersHorizontal,
    color: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400'
  },
  [CalculatorId.INTERQUARTILE_RANGE]: {
    icon: Shrink,
    color: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400'
  },
  [CalculatorId.QUARTILE]: {
    icon: Expand,
    color: 'bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400'
  },
  
  // Graph calculators
  [CalculatorId.BarGraph]: {
    icon: BarChart,
    color: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
  },
  [CalculatorId.ColumnChart]: {
    icon: Columns,
    color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400'
  },
  [CalculatorId.HorizontalBarGraph]: {
    icon: BarChartHorizontal,
    color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400'
  },
  [CalculatorId.LineChart]: {
    icon: LineChart,
    color: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
  },
  [CalculatorId.PieChart]: {
    icon: PieChart,
    color: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400'
  },
  [CalculatorId.ScatterChart]: {
    icon: ScatterChart,
    color: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400'
  },
  [CalculatorId.AreaChart]: {
    icon: AreaChart,
    color: 'bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400'
  },
  [CalculatorId.RadarChart]: {
    icon: Radar,
    color: 'bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400'
  },
  [CalculatorId.DonutChart]: {
    icon: CircleDot,
    color: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400'
  },
  [CalculatorId.Histogram]: {
    icon: BarChart,
    color: 'bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400'
  },
  [CalculatorId.StackedBarChart]: {
    icon: Layers,
    color: 'bg-lime-50 text-lime-600 dark:bg-lime-950 dark:text-lime-400'
  },
  [CalculatorId.NightingaleChart]: {
    icon: Flower2,
    color: 'bg-pink-50 text-pink-600 dark:bg-pink-950 dark:text-pink-400'
  },
  [CalculatorId.CRITICAL_VALUE]: {
    icon: Brackets,
    color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400'
  },
  [CalculatorId.COEFFICIENT_OF_DETERMINATION]: {
    icon: Ratio,
    color: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
  },
  [CalculatorId.DEGREES_OF_FREEDOM]: {
    icon: Sigma,
    color: 'bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400'
  },
  [CalculatorId.SUM_SQUARED_ERRORS]: {
    icon: Calculator,
    color: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400'
  }
};

/**
 * Get the icon and color for a calculator based on its ID or type
 */
export function getCalculatorIcon(calculatorId: string, calculatorType: string): IconInfo {
  // First try to get a specific icon for the calculator ID
  const enumKey = Object.entries(CalculatorId).find(([_, value]) => value === calculatorId)?.[0];
  if (enumKey) {
    const iconInfo = calculatorIdIcons[CalculatorId[enumKey as keyof typeof CalculatorId]];
    if (iconInfo) {
      return iconInfo;
    }
  }
  
  // Fall back to the calculator type icon
  if (calculatorType in calculatorTypeIcons) {
    return calculatorTypeIcons[calculatorType] as IconInfo;
  }
  
  // Default fallback
  return {
    icon: Calculator,
    color: 'bg-gray-50 text-gray-600 dark:bg-gray-950 dark:text-gray-400'
  };
} 